import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { categories, animeList } from '@/mocks/anime';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredAnime = animeList.filter((a) => a.category === activeSlug);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title={`${activeCategory.name}动漫 - 风车动漫`}
        description={`风车动漫${activeCategory.name}分类，${activeCategory.description}。精选${activeCategory.name}类动漫作品推荐、最新资讯和深度评测。`}
        keywords={`${activeCategory.name}动漫,手机动漫,风车动漫,动漫推荐`}
        canonicalUrl={`https://fengchedongman.com/category/${activeSlug}`}
        breadcrumbs={[
          { name: '首页', url: 'https://fengchedongman.com/' },
          { name: `${activeCategory.name}动漫`, url: `https://fengchedongman.com/category/${activeSlug}` },
        ]}
        type="webpage"
      />

      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-sm text-foreground-400" aria-label="面包屑导航">
              <Link to="/" onClick={scrollToTop} className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{activeCategory.name}动漫</span>
            </nav>
          </div>
        </div>

        {/* Category Header */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-6">
              <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${activeCategory.icon} text-2xl md:text-3xl`}></i>
              </span>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{activeCategory.name}动漫</h1>
                <p className="text-sm text-foreground-500 mt-1">{activeCategory.description}</p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => {
                    setActiveSlug(cat.slug);
                    scrollToTop();
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    cat.slug === activeSlug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70 border border-background-200/70'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Anime List */}
        <section className="py-8 md:py-12 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredAnime.length === 0 ? (
              <div className="text-center py-16">
                <span className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-200/70 text-foreground-300 mb-4">
                  <i className="ri-inbox-line text-3xl"></i>
                </span>
                <p className="text-foreground-500">该分类暂无动漫作品</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                {filteredAnime.map((anime) => (
                  <div key={anime.id} className="group cursor-pointer">
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={anime.coverImage}
                        alt={anime.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        title={`${anime.title} - ${activeCategory.name}动漫`}
                      />
                      <div className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs font-bold px-2 py-0.5 rounded-md">
                        {anime.rating}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {anime.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-1">
                      {anime.episodes}集 · {anime.status}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Related Categories */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">探索更多分类</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {categories
                .filter((c) => c.slug !== activeSlug)
                .slice(0, 4)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={scrollToTop}
                    className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="text-sm text-foreground-700 font-medium whitespace-nowrap">{cat.name}</span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}